'use client';
import {
  AssessmentQuestion,
  AssessmentRecord,
  AssessmentSection,
  AssessmentSubmission,
} from '@scaleup/models';
import { createContext, FC, ReactNode, useContext, useEffect, useState } from 'react';
import {
  useGetAssessmentSubmissionQuery,
  useUpdateAssessmentSubmissionMutation,
} from '../../api';

interface AssessmentContextValue {
  submission: AssessmentSubmission | undefined;
  isSubmissionLoading: boolean;
  isUpdateSubmissionLoading: boolean;
  setAnswers: (value: AssessmentSubmission) => void;
  getAssessmentSectionStatus: (
    section: AssessmentSection
  ) => 'pending' | 'inProgress' | 'completed';
  getAssessmentAnsweredStatus: () => boolean;
  getAssessmentCompletionStatus: () => boolean;
  getCurrentSubmissionId: () => string | null;
}

const AssessmentContext = createContext<AssessmentContextValue | undefined>(
  undefined
);

interface AssessmentProviderProps {
  assessmentRecord: AssessmentRecord;
  children: ReactNode;
}

export const AssessmentProvider: FC<AssessmentProviderProps> = ({
  assessmentRecord,
  children,
}) => {
  const [answers, setAnswers] = useState<AssessmentSubmission | undefined>();

  const { data: submission, isLoading: isSubmissionLoading } =
    useGetAssessmentSubmissionQuery(assessmentRecord.assessmentRecordId);

  const [updateSubmission, { isLoading: isUpdateSubmissionLoading }] =
    useUpdateAssessmentSubmissionMutation();

  // if we have a current submission set the current answers to the existing submission
  useEffect(() => {
    if (submission) {
      setAnswers({ ...submission });
    }
  }, [submission]);

  // update the submission with the update answers when they change
  useEffect(() => {
    if (answers && submission && JSON.stringify(answers) !== JSON.stringify(submission)) {
      updateSubmission({
        submissionId: submission!.submissionId,
        submission: answers,
      });
    }
  }, [answers, submission, updateSubmission]);

  const getAssessmentSectionStatus = (
    section: AssessmentSection
  ): 'pending' | 'inProgress' | 'completed' => {
    if (!answers) {
      return 'pending';
    }
    let count = 0;
    const _answers = answers.answers;
    const sectionAnswers = _answers[section.slug];
    if (!sectionAnswers) {
      return 'pending';
    }
    section.questions.forEach((question: AssessmentQuestion) => {
      const questionAnswer = sectionAnswers[question.id];
      if (questionAnswer && questionAnswer.answer.length > 0) {
        count++;
      }
    });

    if (count === section.questions.length) {
      return 'completed';
    }

    if (count > 0) {
      return 'inProgress';
    }

    return 'pending';
  };

  const getAssessmentAnsweredStatus = (): boolean => {
    if (!answers) {
      return false;
    }
    // if any section is not completed return false
    let completed = true;
    Object.keys(answers.sectionStatus).forEach((key) => {
      completed = completed && (answers.sectionStatus[key] || false);
    });

    return completed;
  };

  const getAssessmentCompletionStatus = (): boolean => {
    if (!answers) {
      return false;
    }
    return answers.completed || false;
  };

  const getCurrentSubmissionId = (): string | null => {
    if (!answers) {
      return null;
    }
    return answers.submissionId || null;
  };

  const value: AssessmentContextValue = {
    submission: answers,
    isSubmissionLoading,
    isUpdateSubmissionLoading,
    setAnswers,
    getAssessmentSectionStatus,
    getAssessmentAnsweredStatus,
    getAssessmentCompletionStatus,
    getCurrentSubmissionId,
  };

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessmentContext = (): AssessmentContextValue => {
  const context = useContext(AssessmentContext);
  if (context === undefined) {
    throw new Error('useAssessmentContext must be used within an AssessmentProvider');
  }
  return context;
};
